/**
 * resolveLayers — the single place that decides which EffectLayer[] a scene
 * renders. Per-level editor data (`level.effectLayers`, lands in Phase 4) wins;
 * otherwise the code preset for the level id; otherwise none. This is the bridge
 * that lets the editor override a code preset per scene without any other change.
 *
 * Layer-data precedence (scene-schema T4):
 *   1. `level.effectLayers` — explicit per-level override (editor data) wins outright.
 *   2. else `scene.biome` pinned by name — `presetByBiome(biome)` (authoritative).
 *   3. else the preset for the level id string-match — `presetForLevel(id)`.
 *   4. else none.
 * The biome is authored on the level via `scene.biome` (the editor biome picker
 * writes it). It now selects the layer set directly, independent of the level id
 * — so pinning a biome by name no longer requires the level file to be named to
 * match a `presetForLevel` keyword. The id string-match remains the fallback for
 * legacy levels that carry no `scene.biome`. `scene.*` still supplies the anchors
 * + mood the chosen layers render against (AtmosphereLayer threads `ctx.scene`).
 * Exact connected-map/biome pairs then refine only the selected preset's
 * far plane; explicit layer arrays and different authored biomes stay untouched.
 * Finally `scene.lightAnchors` (additive landmark lights) append to whichever
 * layer set won; a level without anchors gets that set back unchanged.
 */
import { presetByBiome, presetForLevel } from '../../../config/atmosphere/presets.config';
import type { EffectLayer } from '../../../config/atmosphere/effectLayer.types';
import { resolveConnectedMapDepth } from '../../../config/atmosphere/connectedMapDepth';
import type { SceneLightAnchor } from '../../../shared/SceneTypes';
import { withLevelLightAnchors } from './levelLightAnchors';

/** Minimal level shape this needs (id + optional per-level effect layers + scene biome). */
export interface LevelLayerSource {
  id?: string;
  effectLayers?: EffectLayer[];
  scene?: { biome?: string | null; lightAnchors?: SceneLightAnchor[] } | null;
}

export function resolveLayers(level: LevelLayerSource | null | undefined): EffectLayer[] {
  return withLevelLightAnchors(resolveBaseLayers(level), level?.scene?.lightAnchors);
}

function resolveBaseLayers(level: LevelLayerSource | null | undefined): EffectLayer[] {
  if (level?.effectLayers && Array.isArray(level.effectLayers)) return level.effectLayers;
  const byBiome = presetByBiome(level?.scene?.biome);
  // Exact connected-map scenery refines the selected generic far plane only.
  // It never overrides explicit editor layers or a different authored biome.
  if (byBiome) return resolveConnectedMapDepth(level?.id, level?.scene?.biome, byBiome.layers);
  const preset = level?.id ? presetForLevel(level.id) : null;
  return resolveConnectedMapDepth(level?.id, level?.scene?.biome, preset?.layers ?? []);
}

/** The level shape the runtime fetches from MapSystem (PlatformerLevel) — `scene`
 *  + `effectLayers` are what we need to honour the full precedence; `effectLayers`
 *  is opaque (`unknown[]`) in src/shared, read typed here. */
export interface MountLevelSource {
  id?: string;
  effectLayers?: unknown[];
  scene?: { biome?: string | null; lightAnchors?: SceneLightAnchor[] } | null;
}

/**
 * Resolve the layers for a cold-load mount. The `LEVEL_LOADED` event carries only
 * the level *id*, so a naive `resolveLayers({ id })` collapses the precedence to
 * the id string-match and silently drops a level's authored `scene.biome` /
 * `effectLayers` (the Scene FX scene-schema's whole point). This bridges the gap:
 * when the fetched current level matches the loading id, run the FULL precedence
 * against it; otherwise (not yet current / mismatch) fall back to id-routing — so
 * the fix is strictly additive (never worse than before).
 */
export function resolveLayersForMount(
  levelId: string,
  currentLevel: MountLevelSource | null | undefined,
): EffectLayer[] {
  const matches = !!currentLevel && currentLevel.id === levelId;
  if (!matches) return resolveLayers({ id: levelId });
  return resolveLayers({
    id: levelId,
    effectLayers: currentLevel!.effectLayers as EffectLayer[] | undefined,
    scene: currentLevel!.scene ?? undefined,
  });
}
